/**
 * react-hook-form adapters over the UI kit fields (§13.4 form field). Every control gets a stable DOM id
 * (`fieldId(name)`) so validation summaries can link each issue to its exact field (§16.1).
 */
import { Checkbox, Field, Input, Select, Switch } from '@windrop/ui';
import type { ReactNode } from 'react';
import {
  useController,
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
} from 'react-hook-form';

/** DOM id of the control bound to a config path, e.g. `identity.slug` → `field-identity.slug`. */
export const fieldId = (name: string): string => `field-${name}`;

/** Focus and reveal the control for a config path; returns false when it is not mounted (other wizard step). */
export function focusField(name: string): boolean {
  const el = document.getElementById(fieldId(name));
  if (!el) return false;
  el.scrollIntoView({ block: 'center' });
  el.focus();
  return true;
}

interface BaseProps<T extends FieldValues, N extends FieldPath<T>> {
  control: Control<T>;
  name: N;
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  rules?: RegisterOptions<T, N>;
  disabled?: boolean;
  className?: string;
}

export interface RhfTextProps<T extends FieldValues, N extends FieldPath<T>> extends BaseProps<T, N> {
  type?: 'text' | 'email' | 'number' | 'url' | 'datetime-local' | 'date' | 'time' | 'password';
  preset?: 'name' | 'email' | 'tel' | 'otp' | 'postal' | 'handle' | 'number';
  placeholder?: string;
  /** Transform the raw input before it reaches the form (e.g. slugify, uppercase). */
  parse?: (raw: string) => unknown;
  /** Render the form value as the input text (e.g. arrays joined by comma). */
  format?: (value: unknown) => string;
  list?: string;
  autoFocus?: boolean;
}

export function RhfText<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  hint,
  required,
  rules,
  disabled,
  className,
  type = 'text',
  preset,
  placeholder,
  parse,
  format,
  list,
  autoFocus,
}: RhfTextProps<T, N>) {
  const { field, fieldState } = useController({ control, name, rules });
  const value = field.value as unknown;
  const text = format ? format(value) : value === undefined || value === null ? '' : String(value);
  return (
    <Field
      id={fieldId(name)}
      label={label}
      hint={hint}
      required={required}
      error={fieldState.error?.message}
      className={className}
    >
      <Input
        ref={field.ref}
        name={field.name}
        type={type}
        preset={preset}
        placeholder={placeholder}
        value={text}
        onChange={(e) =>
          field.onChange(
            parse ? parse(e.target.value) : type === 'number' ? e.target.valueAsNumber : e.target.value,
          )
        }
        onBlur={field.onBlur}
        disabled={disabled}
        list={list}
        autoFocus={autoFocus}
      />
    </Field>
  );
}

export interface RhfTextareaProps<T extends FieldValues, N extends FieldPath<T>> extends BaseProps<T, N> {
  rows?: number;
  placeholder?: string;
}

export function RhfTextarea<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  hint,
  required,
  rules,
  disabled,
  className,
  rows = 3,
  placeholder,
}: RhfTextareaProps<T, N>) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Field
      id={fieldId(name)}
      label={label}
      hint={hint}
      required={required}
      error={fieldState.error?.message}
      className={className}
    >
      <textarea
        ref={field.ref}
        id={fieldId(name)}
        name={field.name}
        className="wd-input"
        rows={rows}
        placeholder={placeholder}
        value={(field.value as string | undefined) ?? ''}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        disabled={disabled}
        aria-invalid={fieldState.error ? true : undefined}
      />
    </Field>
  );
}

export interface RhfSelectProps<T extends FieldValues, N extends FieldPath<T>> extends BaseProps<T, N> {
  options: ReadonlyArray<{ value: string; label: string; disabled?: boolean }>;
  placeholder?: string;
}

export function RhfSelect<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  hint,
  required,
  rules,
  disabled,
  className,
  options,
  placeholder,
}: RhfSelectProps<T, N>) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Field
      id={fieldId(name)}
      label={label}
      hint={hint}
      required={required}
      error={fieldState.error?.message}
      className={className}
    >
      <Select
        ref={field.ref}
        name={field.name}
        options={options}
        placeholder={placeholder}
        value={(field.value as string | undefined) ?? ''}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        disabled={disabled}
      />
    </Field>
  );
}

export interface RhfCheckboxProps<T extends FieldValues, N extends FieldPath<T>> extends BaseProps<T, N> {
  description?: ReactNode;
}

export function RhfCheckbox<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  description,
  rules,
  disabled,
  className,
}: RhfCheckboxProps<T, N>) {
  const { field, fieldState } = useController({ control, name, rules });
  return (
    <Checkbox
      id={fieldId(name)}
      name={field.name}
      label={label}
      description={description}
      checked={Boolean(field.value)}
      onCheckedChange={(v) => field.onChange(v)}
      disabled={disabled}
      error={fieldState.error?.message}
      className={className}
    />
  );
}

export interface RhfSwitchProps<T extends FieldValues, N extends FieldPath<T>> extends BaseProps<T, N> {
  onLabel?: string;
  offLabel?: string;
}

export function RhfSwitch<T extends FieldValues, N extends FieldPath<T>>({
  control,
  name,
  label,
  rules,
  disabled,
  className,
  onLabel,
  offLabel,
}: RhfSwitchProps<T, N>) {
  const { field } = useController({ control, name, rules });
  return (
    <Switch
      id={fieldId(name)}
      label={label}
      checked={Boolean(field.value)}
      onCheckedChange={(v) => field.onChange(v)}
      disabled={disabled}
      onLabel={onLabel}
      offLabel={offLabel}
      className={className}
    />
  );
}

export interface IssueLink {
  path: string;
  message: string;
  severity?: 'error' | 'warning';
}

/** Error summary: each issue is a link that focuses its field (§13.5 "errors identify the issue and correction"). */
export function FormErrorSummary({
  issues,
  title = 'Fix these before continuing',
  onNavigate,
}: {
  issues: IssueLink[];
  title?: string;
  /** Called when the field is not on the current screen (e.g. another wizard step) so the caller can route. */
  onNavigate?: (path: string) => void;
}) {
  if (issues.length === 0) return null;
  return (
    <section className="wdc-summary" role="alert" aria-labelledby="form-error-summary">
      <h3 id="form-error-summary">{title}</h3>
      <ul>
        {issues.map((i) => (
          <li key={`${i.path}-${i.message}`} data-severity={i.severity ?? 'error'}>
            <a
              href={`#${fieldId(i.path)}`}
              data-path={i.path}
              onClick={(e) => {
                e.preventDefault();
                if (!focusField(i.path)) onNavigate?.(i.path);
              }}
            >
              {i.message}
            </a>{' '}
            <code className="wd-muted">{i.path}</code>
          </li>
        ))}
      </ul>
    </section>
  );
}
